import { RightOutlined } from "@ant-design/icons"
import { Flex, Progress, Typography, theme } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import { CardComponent } from "../../../../components/Card/CardComponent.tsx"
import Icon from "../../../../Icon/index.tsx"
import { RoutesPaths } from "../../../../constants/routes.ts"
import type { GuideLearningArea } from "../../types/guide-content.types.ts"

interface GuideAreaCardProps {
  area: GuideLearningArea
  lessonCount: number
  completedCount: number
}

/** Same restrained card shape throughout Learn EZBuild — icon, title,
 * description, a real count (never a hardcoded mockup number), an optional
 * progress bar once there's something to show progress on. */
export const GuideAreaCard = ({ area, lessonCount, completedCount }: GuideAreaCardProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { token } = theme.useToken()
  const percent = lessonCount ? Math.round((completedCount / lessonCount) * 100) : 0

  return (
    <CardComponent
      hoverable
      className="h-full cursor-pointer"
      onClick={() => navigate(RoutesPaths.guide.module(area.id))}
    >
      <Flex vertical gap={10} className="h-full">
        <Flex
          align="center"
          justify="center"
          className="size-10 rounded-full"
          style={{ background: token.colorPrimaryBg, color: token.colorPrimary }}
        >
          <Icon type={area.icon} />
        </Flex>
        <Typography.Title level={5} className="!mb-0">
          {t(area.titleKey)}
        </Typography.Title>
        <Typography.Text type="secondary" className="text-sm">
          {t(area.descriptionKey)}
        </Typography.Text>
        <Flex align="center" justify="space-between" className="mt-auto pt-2">
          {/* The caller (Browse by Area) only ever passes an area that
              already has at least one accessible lesson — see
              `getAccessibleLearningAreas` — so `lessonCount` is never 0
              here in practice. Nothing renders in that place instead of a
              "coming soon" label if it ever were: no card on this page may
              point at a destination with no real content. */}
          {lessonCount > 0 && (
            <Typography.Text type="secondary" className="text-xs">
              {t("guide.academy.landing.lessonCount", { count: lessonCount })}
            </Typography.Text>
          )}
          <RightOutlined className="text-gray-300" />
        </Flex>
        {lessonCount > 0 && completedCount > 0 && (
          <Progress percent={percent} size="small" showInfo={false} />
        )}
      </Flex>
    </CardComponent>
  )
}
